/**
 * Contact Page
 * Comprehensive contact information for CueSesh
 */

import Header from '../components/Header';
import { MetaTags } from '../components/SEO';
import './Legal.css';

const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "mainEntity": {
    "@type": "Organization",
    "name": "CueSesh",
    "legalName": "CueSesh LLC",
    "url": "https://cuesesh.com",
    "telephone": "+1-917-830-5632",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "7216 Bergenline Ave #1054",
      "addressLocality": "North Bergen",
      "addressRegion": "NJ",
      "postalCode": "07047",
      "addressCountry": "US"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "email": "support@cuesesh.com"
      },
      {
        "@type": "ContactPoint",
        "contactType": "Privacy",
        "email": "privacy@cuesesh.com"
      },
      {
        "@type": "ContactPoint",
        "contactType": "Legal",
        "email": "legal@cuesesh.com"
      }
    ]
  }
};

export default function Contact() {
  return (
    <>
      <MetaTags
        title="Contact"
        description="Get in touch with CueSesh. Contact us for app support, bug reports, privacy requests, or general inquiries."
        canonicalUrl="https://cuesesh.com/contact"
        schema={contactPageSchema}
      />

      <div className="legal-page">
        <Header />

        <div className="legal-page-with-header">
          <div className="legal-container">
            <h1 className="legal-title">Contact Us</h1>
            <p className="legal-subtitle">
              We'd love to hear from you. Choose the best way to reach us below.
            </p>

            <div className="legal-content">

              <div className="legal-highlight">
                <h2 style={{ marginTop: 0 }}>App Support</h2>
                <p>
                  Having trouble with CueSesh? Found a bug? Need help with your account?
                  Our support team is here to help.
                </p>
                <a href="mailto:support@cuesesh.com" className="contact-email-link">
                  support@cuesesh.com
                </a>
                <p style={{ marginTop: '1rem', opacity: 0.7, fontSize: '0.9rem' }}>
                  We typically respond within a couple of days.
                </p>
              </div>

              <h2>Common Questions</h2>

              <h3>How do I delete my account?</h3>
              <p>
                It's self-service: open the app and go to <strong>Settings &rarr; Delete
                account</strong> — no email required. This removes all your data, including
                listening history, ratings, stats, and profile information. No longer have the
                app? Email <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> from your
                account email and we'll handle it. See our{' '}
                <a href="/privacy">Privacy Policy</a> for details.
              </p>

              <h3>How do I report a bug?</h3>
              <p>
                Send us an email at <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> with:
              </p>
              <ul>
                <li>A description of what happened</li>
                <li>What you expected to happen</li>
                <li>Your device model and iOS version</li>
                <li>Screenshots if possible</li>
              </ul>

              <h3>A podcast I listen to isn't available</h3>
              <p>
                CueSesh aggregates podcasts from public RSS feeds. If a podcast isn't showing up,
                let us know at <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> and
                we'll look into adding it.
              </p>

              <h3>How do I request a feature?</h3>
              <p>
                We love hearing your ideas! Email us at{' '}
                <a href="mailto:support@cuesesh.com">support@cuesesh.com</a> with your feature
                request. While we can't promise every feature will be built, we read every
                suggestion and use them to shape our roadmap.
              </p>

              <h2>Other Inquiries</h2>

              <h3>Privacy & Data Requests</h3>
              <p>
                Account deletion is self-service in the app (Settings &rarr; Delete account). For
                data access, correction, or other privacy-related requests, email{' '}
                <a href="mailto:privacy@cuesesh.com">privacy@cuesesh.com</a> — we respond as
                quickly as we can and fulfill verified requests within 30 days. See our{' '}
                <a href="/privacy">Privacy Policy</a> for your full rights.
              </p>

              <h3>Business & Partnerships</h3>
              <p>
                Interested in partnering with CueSesh? Reach out to{' '}
                <a href="mailto:partner@cuesesh.com">partner@cuesesh.com</a> with details about
                your proposal.
              </p>

              <h3>Press & Media</h3>
              <p>
                For press inquiries, interviews, or media coverage, contact{' '}
                <a href="mailto:media@cuesesh.com">media@cuesesh.com</a>.
              </p>

              <h3>Report Illegal Content (EU Digital Services Act)</h3>
              <p>
                Anyone — whether or not you use CueSesh — can report allegedly illegal content
                in our catalog by emailing{' '}
                <a href="mailto:legal@cuesesh.com">legal@cuesesh.com</a>. We confirm receipt
                electronically and review every notice promptly. What to include, and how
                notices are handled, is described in our{' '}
                <a href="/terms">Terms of Service</a>. (This is separate from deleting your own
                account — that's self-service in the app under Settings &rarr; Delete account.)
              </p>

              <h2>Legal</h2>
              <p>
                For legal matters or copyright/DMCA notices, please email{' '}
                <a href="mailto:legal@cuesesh.com">legal@cuesesh.com</a>. For DMCA takedown
                notices, please review the requirements in our{' '}
                <a href="/terms">Terms of Service</a>.
              </p>

              <h2>Business Details</h2>
              <p>
                CueSesh LLC<br />
                7216 Bergenline Ave #1054<br />
                North Bergen, NJ 07047, United States<br />
                Phone: (917) 830-5632
              </p>

              <div className="legal-highlight" style={{ marginTop: '2rem' }}>
                <p style={{ margin: 0 }}>
                  <strong>Building CueSesh for You</strong><br />
                  We're a small team passionate about podcasts. Every email gets read by a real
                  person, and your feedback directly shapes what we build next.
                </p>
              </div>
            </div>
          </div>
        </div>

        <footer className="legal-footer">
          <div className="legal-footer-links">
            <a href="/privacy" className="legal-footer-link">Privacy</a>
            <a href="/terms" className="legal-footer-link">Terms</a>
            <a href="/contact" className="legal-footer-link">Contact</a>
          </div>
          <p className="legal-footer-text">
            © 2026 CueSesh. All rights reserved.
          </p>
        </footer>
      </div>
    </>
  );
}
